/**
 * Read-only capability matrix. It fetches once per (actor, project) identity, parses
 * the answer strictly, and either shows verified rows or one fixed failure note.
 * There is no polling, no retry and no action: only text leaves this component.
 */

import { useEffect, useState } from "react";
import { api } from "../api/client";
import { capabilityIds, Capabilities, parseCapabilities } from "./capabilities";

const UNAVAILABLE_HR = "Sposobnosti nisu dostupne. Zahtjev se neće automatski ponoviti.";
const CELL_FIELDS = ["configured", "granted", "connected", "healthy", "tool_allowed"] as const;

export function CapabilityMatrix({projectId, actorId}: {projectId: string; actorId: string}) {
  const identity = actorId + ":" + projectId;
  const [data, setData] = useState<{identity: string; value: Capabilities} | null>(null);
  const [failedIdentity, setFailedIdentity] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    setFailedIdentity(null);
    (async () => {
      try {
        const value = parseCapabilities(await api.capabilities(projectId, controller.signal));
        if (!alive) return;
        setData({identity, value});
      } catch {
        if (!alive) return;
        setData(null);
        setFailedIdentity(identity);
      }
    })();
    return () => { alive = false; controller.abort(); };
  }, [identity, projectId]);

  const matrix = data && data.identity === identity ? data.value : null;
  const failed = failedIdentity === identity;

  return (
    <section className="mt-4 rounded border border-border p-4" data-testid="capability-matrix">
      <h3 className="text-sm font-semibold">Sposobnosti sustava</h3>
      {failed && <p className="mt-2 rounded border border-destructive/40 bg-destructive/10 p-3 text-sm" role="alert">{UNAVAILABLE_HR}</p>}
      {matrix && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="py-2">Sposobnost</th>{CELL_FIELDS.map((field) => <th key={field}>{field}</th>)}<th>Alati</th></tr>
            </thead>
            <tbody>
              {capabilityIds.map((id) => {
                const entry = matrix.capabilities[id];
                return (
                  <tr key={id} className="border-b border-border/50" data-testid={"cap-" + id}>
                    <th className="py-2 font-normal">
                      {entry.name}
                      {!entry.implemented && <span className="text-xs text-muted-foreground"> · Nije implementirano</span>}
                    </th>
                    {CELL_FIELDS.map((field) => <td key={field} data-field={field}>{entry[field].value ? "Da" : "Ne"}</td>)}
                    <td className="text-xs text-muted-foreground">{entry.tools.join(", ")}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
